import { ref, computed, type Ref, type ComputedRef } from 'vue';

export interface LookupOptions<T> {
  /** Field used as identifier (default: 'id'). */
  idField?: keyof T;
  /** Field shown as label (default: 'name'). */
  labelField?: keyof T;
  /** Format displayed label. Overrides labelField. */
  format?: (item: T) => string;
}

export interface LookupStore<T> {
  items: Ref<T[]>;
  loading: Ref<boolean>;
  error: Ref<unknown>;
  loaded: Ref<boolean>;
  byId: ComputedRef<Map<string | number, T>>;
  options: Required<Omit<LookupOptions<T>, 'format'>> & { format?: (item: T) => string };
  /** Triggers initial fetch if not yet loaded. */
  ensureLoaded(): Promise<void>;
  /** Forces a refetch. */
  reload(): Promise<void>;
  /** Render label for an id. */
  labelOf(id: string | number | null | undefined): string;
}

/**
 * Reactive, lazily-loaded lookup. Used for cross-module lookups
 * (e.g. aggregatorsLookup pulls from SMS API and is consumed by Tariffs/CDP).
 */
export function createLookupStore<T extends Record<string, any>>(
  fetcher: () => Promise<T[]>,
  options: LookupOptions<T> = {},
): LookupStore<T> {
  const items = ref<T[]>([]) as Ref<T[]>;
  const loading = ref(false);
  const error = ref<unknown>(null);
  const loaded = ref(false);
  let inflight: Promise<void> | null = null;

  const resolvedOpts = {
    idField: (options.idField ?? 'id') as keyof T,
    labelField: (options.labelField ?? 'name') as keyof T,
    format: options.format,
  };

  const byId = computed(() => {
    const map = new Map<string | number, T>();
    for (const item of items.value) {
      const id = item[resolvedOpts.idField];
      if (id != null) map.set(id as string | number, item);
    }
    return map;
  });

  async function load() {
    if (inflight) return inflight;
    inflight = (async () => {
      loading.value = true;
      error.value = null;
      try {
        items.value = await fetcher();
        loaded.value = true;
      } catch (e) {
        error.value = e;
      } finally {
        loading.value = false;
        inflight = null;
      }
    })();
    return inflight;
  }

  async function ensureLoaded() {
    if (loaded.value || inflight) {
      if (inflight) await inflight;
      return;
    }
    await load();
  }

  async function reload() {
    loaded.value = false;
    await load();
  }

  function labelOf(id: string | number | null | undefined): string {
    if (id == null) return '';
    const item = byId.value.get(id);
    if (!item) return String(id);
    if (resolvedOpts.format) return resolvedOpts.format(item);
    const v = item[resolvedOpts.labelField];
    return v == null ? String(id) : String(v);
  }

  return { items, loading, error, loaded, byId, options: resolvedOpts, ensureLoaded, reload, labelOf };
}
